import { Tag } from '@components';
import { Box, Collapse, IconButton, Typography, useTheme } from '@mui/material';
import { useFormikContext } from 'formik';
import { ArrowDown2, Trash } from 'iconsax-react';
import { type FC, type ReactElement, useEffect, useMemo, useState } from 'react';

import type { ProjectFormikType } from '@core/formik/project';
import type { Address } from '@core/types/api/Address/Address';
import { type DistanceInfo, getPlantDistanceInfo } from '@core/utils/projectHelpers';
import { useGetPlantsOptionsWithDistances } from '@web/hooks/plantsDistance/useGetPlantsOptionsWithDistances';
import type { QuoteDrawerFormikType } from '@web/pages/Quotes/QuotesDrawer/utils';

import { ExpandMore } from '..';
import { LocalLookupInput } from '../LookupInput/LocalLookupInput';
import type { LookupInputOption } from '../LookupInput/LookupInputSharedComponents';
import { SlabCard } from '../SlabCard/SlabCard';
import { SlabCardBody } from '../SlabCard/SlabCardBody';
import { SlabCardHeader } from '../SlabCard/SlabCardHeader';

type Props = {
  index: number;
  otherSelectedPlantIds: string[];
  onMatchChange: (value: string | null) => void;
  children: ReactElement;
  arrayHelpers: {
    remove: (index: number) => void;
  };
  formikName: string;
  readOnly?: boolean;
  /** Disables only the plant dropdown (e.g. when the plant is auto-synced from another section). */
  plantDisabled?: boolean;
  projectAddress: Partial<Address>;
};

export const PlantAccordionFormik: FC<Props> = ({
  index,
  formikName,
  otherSelectedPlantIds,
  children,
  arrayHelpers,
  onMatchChange,
  readOnly = false,
  plantDisabled,
  projectAddress,
}) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const formikBag = useFormikContext<ProjectFormikType | QuoteDrawerFormikType>();

  const plantFieldValue = formikBag.getFieldProps(formikName).value;
  const plantId: string | null = plantFieldValue.id;
  const plantLabel: string | null = plantFieldValue.option?.label ?? null;

  const hasPlantId = Boolean(plantId);

  useEffect(() => {
    setIsOpen(Boolean(plantId));
  }, [plantId]);

  const {
    options: allPlantOptionsList,
    distances,
    isLoading: isLoadingOptions,
  } = useGetPlantsOptionsWithDistances({ currentAddress: projectAddress });

  // The options only contain non-archived plants, so a selected plant that is missing
  // from them is archived. The list is empty while loading, hence the length check.
  const isArchived =
    Boolean(plantId) &&
    !isLoadingOptions &&
    allPlantOptionsList.length > 0 &&
    !allPlantOptionsList.some((option) => option.value === plantId);

  const plantOptions = useMemo((): LookupInputOption[] => {
    const base = allPlantOptionsList.filter(
      (option) => !otherSelectedPlantIds.includes(option.value),
    );
    // Inject the archived plant so LocalLookupInput can display the name.
    if (isArchived && plantId) {
      const archivedOption: LookupInputOption = {
        value: plantId,
        label: plantLabel ?? plantId,
        sublabels: ['Archived'],
      };
      return [...base, archivedOption];
    }
    return base;
  }, [allPlantOptionsList, otherSelectedPlantIds, isArchived, plantId, plantLabel]);

  let plantDistanceInfo: DistanceInfo | null = null;
  if (plantId) {
    const distance = distances.get(plantId);
    if (distance) {
      plantDistanceInfo = getPlantDistanceInfo(distance);
    }
  }

  return (
    <SlabCard
      sx={{
        borderLeft: `3px solid ${theme.palette.SlabGreen[600]}`,
      }}
    >
      <SlabCardHeader
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'flex-start',
          backgroundColor: theme.palette.SlabGreen[200],
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ExpandMore expand={isOpen} disabled={!hasPlantId} onClick={() => setIsOpen(!isOpen)}>
              <ArrowDown2 />
            </ExpandMore>
            <Box
              sx={{
                backgroundColor: theme.palette.SlabGreen[600],
                width: '20px',
                height: '20px',
                borderRadius: '8px',
                marginRight: '8px',
                marginLeft: '8px',
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant='h6' fontWeight='bold'>
                Location {index + 1}
              </Typography>
              {isArchived && <Tag variant='Tertiary' label='Archived' />}
            </Box>
            <Box sx={{ display: 'flex', width: '18rem', marginLeft: '2rem' }}>
              <LocalLookupInput
                formState={formikBag.values}
                name={
                  formikName as any /* to simplify typing, it requires a complex inferred type instead of string */
                }
                options={plantOptions}
                isLoading={isLoadingOptions}
                sx={{
                  backgroundColor: 'white',
                  '& .MuiFormHelperText-root': {
                    display: 'none', // hides the helper text entirely
                  },
                }}
                onMatchChange={onMatchChange}
                placeholder='Search or select location'
                disabled={readOnly || plantDisabled}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginLeft: 'auto' }}>
              {plantDistanceInfo?.durationText && plantDistanceInfo?.distanceText && (
                <Typography variant='body2' sx={{ color: theme.palette.text.secondary }}>
                  <Typography
                    component='span'
                    sx={{ marginRight: '0.25rem', color: theme.palette.text.secondary }}
                  >
                    Distance:
                  </Typography>
                  <Typography component='span' color={theme.palette.text.primary}>
                    {plantDistanceInfo?.distanceText}
                  </Typography>
                  <Typography component='span' sx={{ marginRight: '0.5rem', marginLeft: '0.5rem' }}>
                    |
                  </Typography>
                  <Typography
                    component='span'
                    sx={{ marginRight: '0.25rem', color: theme.palette.text.secondary }}
                  >
                    Drive time:
                  </Typography>
                  <Typography component='span' color={theme.palette.text.primary}>
                    {plantDistanceInfo?.durationText}
                  </Typography>
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {readOnly ? null : (
                  <IconButton onClick={() => arrayHelpers.remove(index)}>
                    <Trash variant='TwoTone' color={theme.palette.SlabIndigo['700']} />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </SlabCardHeader>
      <Collapse in={isOpen} timeout='auto' unmountOnExit>
        <SlabCardBody>{children}</SlabCardBody>
      </Collapse>
    </SlabCard>
  );
};
